import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { login } from '../services/auth.service';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof schema>;

const APP_INITIALS = 'AV';

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const redirectTo = searchParams.get('redirect');
  const setTokens = useAuthStore((s) => s.setTokens);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  const { isSubmitting } = form.formState;

  async function onSubmit(values: FormValues) {
    try {
      const result = await login(values.email, values.password);

      if (result.requiresEnrollment) {
        if (!result.enrollmentToken) {
          form.setError('root', { message: 'Enrollment token missing from login response.' });
          return;
        }
        navigate(`/mfa/enroll?et=${encodeURIComponent(result.enrollmentToken)}`, {
          replace: true,
        });
        return;
      }
      if (result.requiresMfa) {
        if (!result.challengeToken) {
          form.setError('root', { message: 'Challenge token missing from login response.' });
          return;
        }
        navigate(`/verify-2fa?ct=${encodeURIComponent(result.challengeToken)}`, {
          replace: true,
        });
        return;
      }
      if (!result.accessToken || !result.refreshToken || !result.accessTokenExpiresAt) {
        form.setError('root', { message: 'Login response was incomplete.' });
        return;
      }

      setTokens(
        result.accessToken,
        result.refreshToken,
        new Date(result.accessTokenExpiresAt),
        result.mfaGracePeriodEndsAt ?? null,
      );
      navigate(redirectTo ? decodeURIComponent(redirectTo) : '/', { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        const msg = msgs[0] ?? '';
        const isLocked = /lock/i.test(msg);
        if (isLocked) {
          form.setError('root', { message: msg || 'Account is locked. Please try again later.' });
        } else if (err.response.status === 400 || err.response.status === 401) {
          form.setError('root', { message: msg || 'Incorrect email or password.' });
        } else {
          toast.error(msg || 'An error occurred. Please try again.');
        }
      } else {
        toast.error('Network error. Please check your connection.');
      }
    }
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-muted/30 px-4 pb-4 pt-20 sm:pt-28">
      <Card className="w-full max-w-sm border-border/50 shadow-xl">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#22c55e] shadow-sm">
              <span className="text-lg font-bold text-white">{APP_INITIALS}</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
            <p className="text-sm text-muted-foreground">Sign in to Agrovet ERP</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {reason === 'expired' && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>Your session has expired. Please sign in again.</span>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@agrovet.com"
                        autoComplete="email"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Password</FormLabel>
                      <Link
                        to="/forgot-password"
                        className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="current-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {/lock/i.test(form.formState.errors.root.message ?? '') ? (
                    <Lock className="h-4 w-4 shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 shrink-0" />
                  )}
                  <span>{form.formState.errors.root.message}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
